import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Pause, Play, X, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const VoiceRecognition = ({ onTranscript, fieldName = 'description' }) => {
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef(null);

  // Vocabulaire métier sécurité
  const securityVocabulary = [
    'levée de doute',
    'déclenchement SSI',
    'DM',
    'désenfumage',
    'vol à l\'étalage',
    'rondier',
    'PC sécurité',
    'commission de sécurité',
    'interpellation',
    'RAS',
    'alarme incendie',
    'alarme intrusion',
    'PTI',
    'malaise',
    'accident',
    'zone réservée',
    'contrôle d\'accès',
    'badge visiteur',
    'ronde technique',
    'main courante'
  ];

  useEffect(() => {
    // Vérifier la compatibilité du navigateur
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('La reconnaissance vocale n\'est pas supportée par ce navigateur');
      return;
    }

    // Initialiser Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('Reconnaissance vocale démarrée');
      setIsListening(true);
      setIsPaused(false);
    };

    recognition.onresult = (event) => {
      let interimText = '';
      let finalText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptPart = event.results[i][0].transcript;
        
        if (event.results[i].isFinal) {
          finalText += transcriptPart + ' ';
        } else {
          interimText += transcriptPart;
        }
      }

      if (finalText) {
        // Améliorer le texte avec le vocabulaire métier
        let improvedText = finalText;
        securityVocabulary.forEach(term => {
          const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
          improvedText = improvedText.replace(regex, term);
        });

        // On envoie uniquement le nouveau morceau dicté au parent, qui l'ajoute
        // à son propre texte actuel (préservant ainsi les corrections manuelles).
        // On ne se base jamais sur notre propre mémoire interne pour reconstruire
        // le texte complet, sinon toute correction manuelle serait écrasée.
        onTranscript(improvedText);
        setTranscript(prev => prev + improvedText);
      }

      setInterimTranscript(interimText);
    };

    recognition.onerror = (event) => {
      console.error('Erreur reconnaissance vocale:', event.error);
      if (event.error === 'no-speech') {
        toast.warning('Aucune parole détectée');
      } else if (event.error === 'network') {
        toast.error('Erreur réseau - Vérifiez votre connexion');
      } else {
        toast.error(`Erreur: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      console.log('Reconnaissance vocale terminée');
      if (isListening && !isPaused) {
        // Redémarrer automatiquement si en mode continu
        recognition.start();
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
        toast.success('🎤 Dictée activée - Parlez maintenant', { duration: 2000 });
      } catch (error) {
        console.error('Erreur démarrage:', error);
        toast.error('Impossible de démarrer la reconnaissance vocale');
      }
    }
  };

  const pauseListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsPaused(true);
      setIsListening(false);
      toast.info('⏸️ Dictée en pause');
    }
  };

  const resumeListening = () => {
    if (recognitionRef.current && isPaused) {
      startListening();
      setIsPaused(false);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setIsPaused(false);
      setInterimTranscript('');
      toast.success('✅ Dictée terminée');
    }
  };

  const cancelListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setIsPaused(false);
      setTranscript('');
      setInterimTranscript('');
      onTranscript('');
      toast.info('❌ Dictée annulée');
    }
  };

  return (
    <div className="space-y-3" data-testid="voice-recognition">
      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {!isListening && !isPaused && (
          <Button
            type="button"
            onClick={startListening}
            className="bg-red-500 hover:bg-red-600 text-white"
            size="sm"
            data-testid="start-recording"
          >
            <Mic className="h-4 w-4 mr-2" />
            Dicter
          </Button>
        )}

        {isListening && (
          <>
            <Button
              type="button"
              onClick={pauseListening}
              variant="outline"
              size="sm"
              data-testid="pause-recording"
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </Button>
            <Button
              type="button"
              onClick={stopListening}
              variant="outline"
              size="sm"
              data-testid="stop-recording"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Terminer
            </Button>
          </>
        )}

        {isPaused && (
          <Button
            type="button"
            onClick={resumeListening}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            size="sm"
            data-testid="resume-recording"
          >
            <Play className="h-4 w-4 mr-2" />
            Reprendre
          </Button>
        )}

        {(isListening || isPaused) && (
          <Button
            type="button"
            onClick={cancelListening}
            variant="destructive"
            size="sm"
            data-testid="cancel-recording"
          >
            <X className="h-4 w-4 mr-2" />
            Annuler
          </Button>
        )}

        {/* Status Badge */}
        {isListening && (
          <Badge className="bg-red-500 text-white animate-pulse">
            <Mic className="h-3 w-3 mr-1" />
            Enregistrement en cours
          </Badge>
        )}

        {isPaused && (
          <Badge className="bg-orange-500 text-white">
            <Pause className="h-3 w-3 mr-1" />
            En pause
          </Badge>
        )}
      </div>

      {/* Interim transcript (temps réel) */}
      {interimTranscript && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900 italic">
            {interimTranscript}
            <span className="inline-block w-2 h-4 bg-blue-500 ml-1 animate-pulse"></span>
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceRecognition;
